import { ReactNode, createContext, useContext, useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

interface AccessibilityState {
  highContrast: boolean;
  reducedMotion: boolean;
  fontSize: 'small' | 'medium' | 'large' | 'xl';
  focusRingVisible: boolean;
  screenReaderAnnouncements: boolean;
  keyboardNavigation: boolean;
}

interface AccessibilityActions {
  setHighContrast: (enabled: boolean) => void;
  setReducedMotion: (enabled: boolean) => void;
  setFontSize: (size: 'small' | 'medium' | 'large' | 'xl') => void;
  setFocusRingVisible: (visible: boolean) => void;
  setScreenReaderAnnouncements: (enabled: boolean) => void;
  setKeyboardNavigation: (enabled: boolean) => void;
  resetToDefaults: () => void;
}

type AccessibilityStore = AccessibilityState & AccessibilityActions;

const defaultState: AccessibilityState = {
  highContrast: false,
  reducedMotion: false,
  fontSize: 'medium',
  focusRingVisible: true,
  screenReaderAnnouncements: true,
  keyboardNavigation: true,
};

const useAccessibilityStore = create<AccessibilityStore>()(
  persist(
    immer((set) => ({
      ...defaultState,

      setHighContrast: (enabled) =>
        set((state) => {
          state.highContrast = enabled;
        }),

      setReducedMotion: (enabled) =>
        set((state) => {
          state.reducedMotion = enabled;
        }),

      setFontSize: (size) =>
        set((state) => {
          state.fontSize = size;
        }),

      setFocusRingVisible: (visible) =>
        set((state) => {
          state.focusRingVisible = visible;
        }),

      setScreenReaderAnnouncements: (enabled) =>
        set((state) => {
          state.screenReaderAnnouncements = enabled;
        }),

      setKeyboardNavigation: (enabled) =>
        set((state) => {
          state.keyboardNavigation = enabled;
        }),

      resetToDefaults: () =>
        set((state) => {
          Object.assign(state, defaultState);
        }),
    })),
    {
      name: 'civista_accessibility_preferences',
    }
  )
);

const AccessibilityContext = createContext<AccessibilityStore | null>(null);

interface AccessibilityProviderProps {
  children: ReactNode;
}

export const AccessibilityProvider = ({ children }: AccessibilityProviderProps) => {
  const store = useAccessibilityStore();

  // Apply accessibility settings to the document
  useEffect(() => {
    const root = document.documentElement;

    // High contrast
    if (store.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }

    // Reduced motion
    if (store.reducedMotion) {
      root.classList.add('reduce-motion');
    } else {
      root.classList.remove('reduce-motion');
    }

    // Font size
    root.classList.remove('font-small', 'font-medium', 'font-large', 'font-xl');
    root.classList.add(`font-${store.fontSize}`);

    // Focus ring visibility
    if (store.focusRingVisible) {
      root.classList.add('focus-visible');
    } else {
      root.classList.remove('focus-visible');
    }
  }, [store.highContrast, store.reducedMotion, store.fontSize, store.focusRingVisible]);

  // Detect system preferences
  useEffect(() => {
    // Check for system reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion && !store.reducedMotion) {
      store.setReducedMotion(true);
    }

    // Check for system high contrast preference
    const prefersHighContrast = window.matchMedia('(prefers-contrast: high)').matches;
    if (prefersHighContrast && !store.highContrast) {
      store.setHighContrast(true);
    }

    // Listen for changes
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const highContrastQuery = window.matchMedia('(prefers-contrast: high)');

    const handleReducedMotionChange = (e: MediaQueryListEvent) => {
      store.setReducedMotion(e.matches);
    };

    const handleHighContrastChange = (e: MediaQueryListEvent) => {
      store.setHighContrast(e.matches);
    };

    reducedMotionQuery.addEventListener('change', handleReducedMotionChange);
    highContrastQuery.addEventListener('change', handleHighContrastChange);

    return () => {
      reducedMotionQuery.removeEventListener('change', handleReducedMotionChange);
      highContrastQuery.removeEventListener('change', handleHighContrastChange);
    };
  }, [store]);

  // Keyboard navigation detection
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        store.setKeyboardNavigation(true);
      }
    };

    const handleMouseDown = () => {
      store.setKeyboardNavigation(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [store]);

  return (
    <AccessibilityContext.Provider value={store}>
      {children}
    </AccessibilityContext.Provider>
  );
};

export const useAccessibility = () => {
  const context = useContext(AccessibilityContext);
  if (!context) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};